/**
 * Erişim token'ının SESSİZ yenilenmesi.
 *
 * Talep: "Kullanıcı bazen sebepsiz sistemden atılıyor." Kök neden: giriş yanıtındaki
 * refresh token localStorage'a yazılıyordu ama hiçbir yerde KULLANILMIYORDU. Erişim
 * token'ının ömrü dolunca (appsettings `TokenOptions.AccessTokenExpiration`) tek sonuç
 * çıkıştı — kullanıcı ekranın ortasında, hiçbir şey yapmadan login'e atılıyordu.
 *
 * Bu modül yenilemenin TEK yoludur ve iki yerden çağrılır:
 *   • {@link ../authInterceptor} — bir istek 401 dönünce (tepkisel),
 *   • {@link ../components/SessionGuard} — "exp" yaklaşınca (önleyici).
 *
 * ## Tek uçuş (single-flight)
 * Bir menü tıklaması birkaç isteği birden açar; hepsi aynı anda 401 alabilir. Rotasyon
 * gereği her yenileme eski refresh token'ı ANINDA geçersiz kılar (bkz. sunucu tarafı
 * `LoginSecurityService.RotateSessionAsync`), yani paralel iki yenileme birbirinin
 * token'ını çöpe atıp oturumu gerçekten öldürürdü. Bu yüzden uçuştaki istek paylaşılır.
 *
 * ## SEKMELER ARASI tek uçuş — kalan boşluk buydu
 * Yukarıdaki `inFlight` bir MODÜL değişkenidir: yalnız kendi sekmesini kapsar. Refresh token
 * ise `localStorage`'dadır ve TÜM sekmeler tarafından paylaşılır. Panel iki sekmede açıkken
 * (sipariş formu bir sekmede, liste/rapor diğerinde — sahada olağan kullanım) ikisi de aynı
 * anda yenilemeye kalkar:
 *
 *   Sekme A: refresh(R0) → sunucu R0'ı ROTE eder, R1 döner, localStorage = R1
 *   Sekme B: refresh(R0) → R0 artık DB'de yok → 401 → `rejected: true` → ÇIKIŞ
 *
 * Sunucu tarafı rotasyon atomik de değildir (`RotateSessionAsync` oku-yaz yapar, CAS/rowversion
 * yok), yani ters sırada da kaybedilebilir. Sonuç kullanıcı için hep aynı: iş yaparken
 * "sebepsiz" login ekranı — üstelik bir sekmedeki çıkış paylaşılan storage'ı temizlediği için
 * DİĞER sekmeleri de düşürür.
 *
 * Çözüm: yenileme **Web Locks** ile tarayıcı genelinde tekilleştirilir. Kilidi alan sekme
 * yeniler; bekleyen sekme kilidi alınca storage'a BAKAR — token zaten tazelenmişse sunucuya
 * hiç gitmez. Bu "önce kontrol et, sonra uygula" adımı kilidin asıl işidir; kilit tek başına
 * yalnız sıraya sokardı, ikinci istek yine ölü token ile giderdi.
 *
 * `navigator.locks` yoksa (eski tarayıcı / güvensiz bağlam) eski davranışa düşülür — yarış
 * ihtimali geri gelir ama akış çalışmaya devam eder.
 *
 * ## "Yenilenemedi" ile "reddedildi" AYNI ŞEY DEĞİLDİR
 * Ağ kesintisinde kullanıcıyı atmak, düzeltmeye çalıştığımız hatanın ta kendisidir.
 * Bu yüzden sonuç iki bilgi taşır: `token` (yenilendi) ve `rejected` (sunucu "bu oturum
 * bitti" dedi). İkisi de boşsa durum GEÇİCİDİR — çağıran oturumu kapatmaz, sonraki
 * denemeye bırakır.
 */

const REFRESH_ENDPOINT = '/api/auth/refresh';

export const TOKEN_KEY = 'token';
export const REFRESH_TOKEN_KEY = 'refreshToken';

/** Sekmeler arası yenileme kilidinin adı (Web Locks). */
const REFRESH_LOCK = 'datascope-token-refresh';

/**
 * Kilidi alan sekme, storage'daki erişim token'ının ömrü bu eşiğin üstündeyse yenilemeyi
 * ATLAR — demek ki beklerken başka bir sekme çoktan yenilemiştir. Eşik, SessionGuard'ın
 * önleyici yenileme penceresinden (2 dk) kısa olmalıdır; aksi halde "taze" saydığımız bir
 * token'la çıkıp hemen yeniden yenileme turuna girerdik.
 */
const FRESH_ENOUGH_MS = 60 * 1000;

/**
 * Yenileme isteğinin gideceği fetch. Varsayılan `window.fetch`'tir, ancak
 * authInterceptor kurulurken KENDİNDEN ÖNCEKİ fetch ile değiştirilir: yenilemenin kendi
 * 401'i yeni bir yenileme denemesi tetiklemesin (sonsuz döngü). apiBase'in rewrite'lı
 * fetch'i o zincirde olduğu için ayrı bir ortam (REACT_APP_API_BASE_URL) yine çalışır.
 */
let transport = (...args) => window.fetch(...args);

export function setRefreshTransport(fetchFn) {
  if (typeof fetchFn === 'function') transport = fetchFn;
}

let inFlight = null;

function read(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null; // private mode / kota
  }
}

/** Yenilenen token'ları giriş akışıyla AYNI anahtarlara yazar (bkz. components/Login.js). */
function persist(data) {
  try {
    localStorage.setItem(TOKEN_KEY, data.accessToken);
    if (data.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);

    // 'user' kaydı token'ın bir KOPYASINI taşır; güncellenmezse bayat token ile
    // çalışan okuyucular kalır. Yenileme yanıtı menü/yetki alanlarını içermediği için
    // mevcut kayıt korunur, yalnız token alanları tazelenir.
    const raw = localStorage.getItem('user');
    if (raw) {
      const user = JSON.parse(raw);
      localStorage.setItem('user', JSON.stringify({
        ...user,
        token: data.accessToken,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken ?? user.refreshToken,
      }));
    }
  } catch { /* yazamadıysak token bellekte geçerli; sonraki istek yine dener */ }
}

async function requestNewToken() {
  const refreshToken = read(REFRESH_TOKEN_KEY);
  // Refresh token yoksa yenilenecek bir şey yok: 401 gerçektir, oturum bitmiştir.
  // (Eski oturumlar ve refresh token döndürmeyen giriş yolları bu dala düşer.)
  if (!refreshToken) return { token: null, rejected: true };

  let response;
  try {
    response = await transport(REFRESH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    return { token: null, rejected: false }; // ağa çıkılamadı — GEÇİCİ
  }

  // Yalnız 401 "bu oturum bitti" demektir. 429 (yenileme ucu rate limit'e tabidir),
  // 5xx ve gövde hataları geçicidir; onlarda kullanıcıyı atmak yeni bir hata olurdu.
  if (response.status === 401) return { token: null, rejected: true };
  if (!response.ok) return { token: null, rejected: false };

  const result = await response.json().catch(() => null);
  // Yenileme ucu da giriş ucu gibi ApiResponse zarfı KULLANMAZ, doğrudan yanıt döner.
  const data = result?.data ?? result;
  if (!data?.accessToken) return { token: null, rejected: false };

  persist(data);
  return { token: data.accessToken, rejected: false };
}

/**
 * `token`'ın "exp" claim'ini okur. Bozuk/exp'siz token için null döner.
 * (utils/auth `getTokenExp` storage'dan okur; burada elimizdeki DEĞERİ çözmemiz gerekiyor.)
 */
function readExp(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const seconds = Number(payload?.exp);
    return Number.isFinite(seconds) ? seconds * 1000 : null;
  } catch {
    return null;
  }
}

/** Storage'daki erişim token'ı, sunucuya gitmeye gerek kalmayacak kadar taze mi? */
function storedTokenIsFresh() {
  const token = read(TOKEN_KEY);
  if (!token) return null;
  const exp = readExp(token);
  // "exp"i okunamayan token'a taze diyemeyiz — normal yenileme yoluna düşsün.
  if (exp == null) return null;
  return exp - Date.now() > FRESH_ENOUGH_MS ? token : null;
}

/**
 * Yenilemeyi sekmeler arası kilit altında yürütür. Kilidi alınca ÖNCE storage'a bakar:
 * beklerken başka sekme yenilediyse sunucuya hiç gidilmez (ve o sekmenin token'ı rote
 * edilmez). Web Locks desteklenmiyorsa doğrudan yenilemeye düşer.
 */
function requestNewTokenExclusively() {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
  if (!locks?.request) return requestNewToken();

  return locks.request(REFRESH_LOCK, async () => {
    const fresh = storedTokenIsFresh();
    if (fresh) return { token: fresh, rejected: false };
    return requestNewToken();
  });
}

/**
 * Erişim token'ını yeniler. Eşzamanlı çağrılar tek isteği paylaşır (sekme içinde `inFlight`,
 * sekmeler arasında Web Locks).
 *
 * @returns {Promise<{token: string|null, rejected: boolean}>}
 *   `token` dolu → yenilendi, isteğe devam edilebilir.
 *   `rejected` true → sunucu reddetti; oturum GERÇEKTEN bitti, çıkış yapılmalı.
 *   ikisi de boş → geçici engel; oturumu KAPATMA, sonraki denemeye bırak.
 */
export function refreshAccessToken() {
  if (inFlight) return inFlight;
  inFlight = requestNewTokenExclusively()
    // Beklenmedik bir istisna çağıranın destructuring'ini patlatmasın; sonuç her
    // zaman bir nesnedir ve bilinmeyen hata GEÇİCİ sayılır (oturumu kapatmaz).
    .catch(() => ({ token: null, rejected: false }))
    .finally(() => { inFlight = null; });
  return inFlight;
}

export default refreshAccessToken;
